/**
 * Shared localized content for multi-component structured data (visas,
 * services). Each field is a *lazy* thunk over a Paraglide message function.
 *
 * IMPORTANT: defining the thunks at module scope is safe — the message
 * functions are NOT called here. They are only called when a component
 * invokes the thunk during render (per-request / per-page locale context),
 * which avoids the classic "module-scope m.*() caches the wrong locale"
 * footgun with prerendering.
 */

import { m } from '$lib/paraglide/messages.js';
import type { LocalizedString } from '$lib/paraglide/runtime.js';
import type { ServiceId } from '$data/services';
import type { VisaId } from '$data/visa-data';

type Msg = () => LocalizedString;

export interface VisaPointText {
	label: Msg;
	text: Msg;
}

export interface VisaNoteText {
	text: Msg;
	tone: 'gold' | 'muted';
}

export interface VisaWorkflowStepText {
	step: string;
	text: Msg;
}

export interface VisaText {
	subtitle: Msg;
	description: Msg;
	cardDetails: Msg;
	heading: Msg;
	cta: Msg;
	points: VisaPointText[];
	note?: VisaNoteText;
	extraHeading?: Msg;
	extraPoints?: VisaPointText[];
	paragraphs?: Msg[];
	workflow?: VisaWorkflowStepText[];
}

export const VISA_TEXT: Record<VisaId, VisaText> = {
	eb1: {
		subtitle: m.visa_eb1_subtitle,
		description: m.visa_eb1_description,
		cardDetails: m.visa_eb1_carddetails,
		heading: m.visa_eb1_heading,
		cta: m.visa_eb1_cta,
		points: [
			{ label: m.visa_eb1_point_0_label, text: m.visa_eb1_point_0_text },
			{ label: m.visa_eb1_point_1_label, text: m.visa_eb1_point_1_text },
			{ label: m.visa_eb1_point_2_label, text: m.visa_eb1_point_2_text }
		]
	},
	eb2niw: {
		subtitle: m.visa_eb2niw_subtitle,
		description: m.visa_eb2niw_description,
		cardDetails: m.visa_eb2niw_carddetails,
		heading: m.visa_eb2niw_heading,
		cta: m.visa_eb2niw_cta,
		points: [
			{ label: m.visa_eb2niw_point_0_label, text: m.visa_eb2niw_point_0_text },
			{ label: m.visa_eb2niw_point_1_label, text: m.visa_eb2niw_point_1_text },
			{ label: m.visa_eb2niw_point_2_label, text: m.visa_eb2niw_point_2_text }
		]
	},
	eb3: {
		subtitle: m.visa_eb3_subtitle,
		description: m.visa_eb3_description,
		cardDetails: m.visa_eb3_carddetails,
		heading: m.visa_eb3_heading,
		cta: m.visa_eb3_cta,
		points: [
			{ label: m.visa_eb3_point_0_label, text: m.visa_eb3_point_0_text },
			{ label: m.visa_eb3_point_1_label, text: m.visa_eb3_point_1_text },
			{ label: m.visa_eb3_point_2_label, text: m.visa_eb3_point_2_text }
		],
		note: { text: m.visa_eb3_note, tone: 'muted' }
	},
	eb4: {
		subtitle: m.visa_eb4_subtitle,
		description: m.visa_eb4_description,
		cardDetails: m.visa_eb4_carddetails,
		heading: m.visa_eb4_heading,
		cta: m.visa_eb4_cta,
		points: [
			{ label: m.visa_eb4_point_0_label, text: m.visa_eb4_point_0_text },
			{ label: m.visa_eb4_point_1_label, text: m.visa_eb4_point_1_text },
			{ label: m.visa_eb4_point_2_label, text: m.visa_eb4_point_2_text }
		]
	},
	eb5: {
		subtitle: m.visa_eb5_subtitle,
		description: m.visa_eb5_description,
		cardDetails: m.visa_eb5_carddetails,
		heading: m.visa_eb5_heading,
		cta: m.visa_eb5_cta,
		points: [
			{ label: m.visa_eb5_point_0_label, text: m.visa_eb5_point_0_text },
			{ label: m.visa_eb5_point_1_label, text: m.visa_eb5_point_1_text },
			{ label: m.visa_eb5_point_2_label, text: m.visa_eb5_point_2_text }
		],
		extraHeading: m.visa_eb5_extra_heading,
		workflow: [
			{ step: '1', text: m.visa_eb5_workflow_0 },
			{ step: '2', text: m.visa_eb5_workflow_1 },
			{ step: '3', text: m.visa_eb5_workflow_2 },
			{ step: '4', text: m.visa_eb5_workflow_3 },
			{ step: '5', text: m.visa_eb5_workflow_4 }
		]
	},
	l1: {
		subtitle: m.visa_l1_subtitle,
		description: m.visa_l1_description,
		cardDetails: m.visa_l1_carddetails,
		heading: m.visa_l1_heading,
		cta: m.visa_l1_cta,
		points: [
			{ label: m.visa_l1_point_0_label, text: m.visa_l1_point_0_text },
			{ label: m.visa_l1_point_1_label, text: m.visa_l1_point_1_text }
		],
		note: { text: m.visa_l1_note, tone: 'gold' },
		paragraphs: [m.visa_l1_paragraph_0]
	},
	o1: {
		subtitle: m.visa_o1_subtitle,
		description: m.visa_o1_description,
		cardDetails: m.visa_o1_carddetails,
		heading: m.visa_o1_heading,
		cta: m.visa_o1_cta,
		points: [
			{ label: m.visa_o1_point_0_label, text: m.visa_o1_point_0_text },
			{ label: m.visa_o1_point_1_label, text: m.visa_o1_point_1_text }
		],
		extraHeading: m.visa_o1_extra_heading,
		paragraphs: [m.visa_o1_paragraph_0, m.visa_o1_paragraph_1]
	},
	e2: {
		subtitle: m.visa_e2_subtitle,
		description: m.visa_e2_description,
		cardDetails: m.visa_e2_carddetails,
		heading: m.visa_e2_heading,
		cta: m.visa_e2_cta,
		points: [
			{ label: m.visa_e2_point_0_label, text: m.visa_e2_point_0_text },
			{ label: m.visa_e2_point_1_label, text: m.visa_e2_point_1_text }
		],
		extraHeading: m.visa_e2_extra_heading,
		extraPoints: [
			{ label: m.visa_e2_extra_point_0_label, text: m.visa_e2_extra_point_0_text },
			{ label: m.visa_e2_extra_point_1_label, text: m.visa_e2_extra_point_1_text },
			{ label: m.visa_e2_extra_point_2_label, text: m.visa_e2_extra_point_2_text },
			{ label: m.visa_e2_extra_point_3_label, text: m.visa_e2_extra_point_3_text }
		]
	}
};

export interface ServiceText {
	title: Msg;
	overview: Msg;
	grid: Msg;
}

export const SERVICE_TEXT: Record<ServiceId, ServiceText> = {
	visa: { title: m.service_visa_title, overview: m.service_visa_overview, grid: m.service_visa_grid },
	business: {
		title: m.service_business_title,
		overview: m.service_business_overview,
		grid: m.service_business_grid
	},
	strategy: {
		title: m.service_strategy_title,
		overview: m.service_strategy_overview,
		grid: m.service_strategy_grid
	},
	support: {
		title: m.service_support_title,
		overview: m.service_support_overview,
		grid: m.service_support_grid
	}
};
